import { useNavigate } from 'react-router'
import { Avatar, Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { useAuth } from '@/app/auth/auth-context'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const SECURITY = [
  { icon: 'password', title: 'Password', body: 'Changed 3 months ago' },
  { icon: 'phonelink_lock', title: 'Two-factor authentication', body: 'On · authenticator app' },
  { icon: 'face', title: 'Face ID on this browser', body: 'On' },
  { icon: 'devices', title: 'Trusted devices', body: '2 devices' },
]

interface SettingRow {
  readonly icon: string
  readonly title: string
  readonly body: string
  readonly to?: string
}

const PLAN_AND_LIMITS: readonly SettingRow[] = [
  { icon: 'workspace_premium', title: 'Your plan', body: 'Free · see what Plus would have saved you', to: '/plans' },
  { icon: 'speed', title: 'Payment limits', body: '$1,000,000 monthly · $10,000 daily' },
  { icon: 'redeem', title: 'Cashback', body: '$47.20 earned this year', to: '/cashback' },
  { icon: 'download', title: 'Data export', body: 'Download everything we hold' },
]

const ACTIVITY = [
  ['Logged in', 'Chrome on macOS · today'],
  ['Password changed', '3 months ago'],
  ['New device added', 'iPhone · 12 Aug'],
  ['KYC tier upgraded', 'Tier 2 · 4 Jul'],
] as const

export function ProfileScreen() {
  const repository = useRepository()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const user = useQuery(() => repository.getUser(), [repository])
  const name = user.data ? `${user.data.firstName} ${user.data.lastName}` : 'Loading…'

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <>
      <PageHeader
        title="Profile & security"
        subtitle="Your details, devices and the limits on your account."
        actions={
          <Button variant="outline" size="md" iconStart="logout" onClick={handleLogout}>
            Log out
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={3}>
          <Card tone="light">
            <Stack direction="row" align="center" gap={4}>
              <Avatar name={name} size={56} />
              <Stack grow gap={1}>
                <Stack direction="row" align="center" gap={2} wrap>
                  <Text variant="subtitle" weight="bold">{name}</Text>
                  {user.data?.verified && <Pill tone="positive">Tier {user.data.tier} · verified</Pill>}
                </Stack>
                <Text variant="caption" tone="secondary">{user.data?.email}</Text>
              </Stack>
              <Button variant="outline" size="sm" to="/settings">Edit</Button>
            </Stack>
          </Card>
        </BentoSpan>

        <Card tone="dark">
          <Text variant="caption" weight="semibold" tone="onDark">Recent security activity</Text>
          <Stack gap={3} className="pb-mt-4">
            {ACTIVITY.map(([what, when]) => (
              <Stack key={what} gap={0}>
                <Text variant="caption" weight="semibold" tone="onDark">{what}</Text>
                <Text variant="eyebrow" tone="onDarkMuted">{when}</Text>
              </Stack>
            ))}
          </Stack>
        </Card>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">Security</Text>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {SECURITY.map((row) => (
                <button key={row.title} type="button" className={checkout.reason} style={{ width: '100%', textAlign: 'left' }}>
                  <Icon name={row.icon} size={20} />
                  <Stack grow gap={1}>
                    <Text variant="caption" weight="semibold">{row.title}</Text>
                    <Text variant="eyebrow" tone="secondary">{row.body}</Text>
                  </Stack>
                  <Icon name="chevron_right" size={18} />
                </button>
              ))}
            </div>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">Plan, privacy and limits</Text>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {PLAN_AND_LIMITS.map((row) => (
                <button
                  key={row.title}
                  type="button"
                  className={checkout.reason}
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => row.to && navigate(row.to)}
                >
                  <Icon name={row.icon} size={20} />
                  <Stack grow gap={1}>
                    <Text variant="caption" weight="semibold">{row.title}</Text>
                    <Text variant="eyebrow" tone="secondary">{row.body}</Text>
                  </Stack>
                  <Icon name="chevron_right" size={18} />
                </button>
              ))}
            </div>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
